import { useState } from "react";
import { FileText, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { API } from "@/lib/api";

const FIELDS = [
  ["client_name", "Prepared for (client name)", "e.g. Jordan Lee", 80],
  ["advisor_name", "Advisor name", "e.g. Sam Patel", 80],
  ["firm", "Firm", "e.g. Northshore Wealth", 100],
  ["contact", "Advisor contact (email or phone)", "e.g. sam@firm.ca · 416-555-0134", 120],
];

const download = async (messageId, body) => {
  const r = await fetch(`${API}/brief/${messageId}.pdf`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  if (!r.ok) throw new Error("brief");
  const url = URL.createObjectURL(await r.blob());
  const a = Object.assign(document.createElement("a"), { href: url, download: `Harvest-Advisor-Brief-${messageId.slice(0, 8)}.pdf` });
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
};

export const BriefDialog = ({ messageId, trigger }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ client_name: "", advisor_name: "", firm: "", contact: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const run = async (body) => {
    setBusy(true);
    setErr("");
    try {
      await download(messageId, body);
      setOpen(false);
    } catch {
      setErr("Sorry, the brief couldn't be created. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <span onClick={() => setOpen(true)}>{trigger}</span>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none border-hv-rule sm:max-w-md" data-testid="brief-dialog">
          <DialogHeader className="text-left sm:text-left">
            <DialogTitle className="flex items-center gap-2 font-heading text-hv-navy"><FileText size={18} aria-hidden="true" />Advisor brief</DialogTitle>
            <DialogDescription className="text-[13px] text-hv-muted">
              A one-page PDF of this answer with sources and disclaimers. Add who it's for and your contact line (optional).
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={(e) => { e.preventDefault(); run(form); }} className="space-y-3">
            {FIELDS.map(([k, label, ph, max]) => (
              <label key={k} className="block">
                <span className="font-heading text-[11px] font-semibold uppercase tracking-wider text-hv-muted">{label}</span>
                <input value={form[k]} maxLength={max} placeholder={ph} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                  data-testid={`brief-${k.replace("_", "-")}-input`}
                  className="mt-1 w-full border border-hv-rule px-3 py-2 text-[14px] focus:border-hv-navy focus:outline-none" />
              </label>
            ))}
            <p className="text-[11px] text-hv-muted">These details are printed on the PDF only. Ceres does not store them.</p>
            {err && <p role="alert" className="text-[12px] font-semibold text-hv-navy" data-testid="brief-error">{err}</p>}
            <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
              <button type="button" disabled={busy} onClick={() => run({})} className="text-[13px] text-hv-logo-navy underline disabled:opacity-40" data-testid="brief-skip-button">
                Skip and download
              </button>
              <button type="submit" disabled={busy} className="hv-btn-light inline-flex items-center gap-2" data-testid="brief-download-button">
                {busy && <Loader2 size={14} className="animate-spin" aria-hidden="true" />}Download PDF
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};
